import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Lock, MapPin, Layers, Coins, Eye } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const fmtFjd = (n) =>
    new Intl.NumberFormat("en-FJ", { style: "currency", currency: "FJD", maximumFractionDigits: 0 }).format(n || 0);

export default function PrivateListing() {
    const { token } = useParams();
    const [listing, setListing] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/listings/private/${token}`);
                setListing(res.data);
            } catch (err) {
                setError(err.response?.status === 404 ? "not_found" : "error");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    if (loading) {
        return (
            <div className="bg-background min-h-screen grid place-items-center">
                <div className="text-sm font-app text-muted-foreground">Loading private listing…</div>
            </div>
        );
    }

    if (error === "not_found") {
        return (
            <div className="bg-background min-h-screen grid place-items-center px-4">
                <Card className="p-8 max-w-lg text-center bg-card" data-testid="private-listing-not-found">
                    <Lock className="h-10 w-10 text-coral mx-auto mb-4" />
                    <h1 className="font-display text-2xl font-bold text-foreground mb-2">Listing not available</h1>
                    <p className="font-app text-sm text-muted-foreground mb-5">
                        This private listing link is no longer valid, or the listing has been withdrawn.
                    </p>
                    <Button asChild className="bg-coral hover:bg-coral-deep text-white">
                        <Link to="/marketplace">Browse public marketplace</Link>
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div data-testid="private-listing-page" className="bg-background min-h-screen">
            <section className="bg-ocean-deep-grad text-white py-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Button asChild variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 font-app mb-3">
                        <Link to="/marketplace">
                            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to marketplace
                        </Link>
                    </Button>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-coral/15 border border-coral/30 mb-4">
                        <Lock className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            Off-market · private link
                        </span>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-3">
                        {listing.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 font-app text-sm text-white/80">
                        <span className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            {listing.location_region}, {listing.location_island}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Coins className="h-3.5 w-3.5" />
                            <span className="font-semibold tabular-nums">{fmtFjd(listing.price_fjd)}</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Layers className="h-3.5 w-3.5" />
                            {listing.land_size_acres} acres · {listing.zoning}
                        </span>
                    </div>
                </div>
            </section>

            <section className="py-10 lg:py-12">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        {(listing.external_photos?.length || listing.photos?.length) ? (
                            <div className="grid grid-cols-2 gap-2">
                                {(listing.external_photos || []).slice(0, 4).map((src, i) => (
                                    <img
                                        key={i}
                                        src={src}
                                        alt=""
                                        className="rounded-md object-cover w-full aspect-video"
                                        data-testid={`private-listing-photo-${i}`}
                                    />
                                ))}
                            </div>
                        ) : null}

                        <Card className="p-5 lg:p-6 bg-card" data-testid="private-listing-description">
                            <h2 className="font-display text-lg font-bold text-foreground mb-3">About this listing</h2>
                            <p className="font-app text-sm text-foreground/85 leading-relaxed whitespace-pre-line">
                                {listing.description}
                            </p>
                        </Card>

                        <div className="grid sm:grid-cols-2 gap-3">
                            <Card className="p-4 bg-card">
                                <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">Ownership</div>
                                <div className="font-display text-sm font-bold text-foreground mt-1 capitalize">
                                    {listing.ownership_type?.replace(/_/g, " ")}
                                </div>
                            </Card>
                            <Card className="p-4 bg-card">
                                <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">Development potential</div>
                                <div className="font-app text-sm text-foreground mt-1">{listing.development_potential}</div>
                            </Card>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Card className="p-5 bg-coral/5 dark:bg-coral/10 border-coral/30" data-testid="private-listing-cta">
                            <div className="flex items-center gap-2 mb-3">
                                <Lock className="h-4 w-4 text-coral" />
                                <Badge variant="outline" className="text-[10px] border-coral/40 text-coral">
                                    Private listing
                                </Badge>
                            </div>
                            <div className="font-display text-base font-bold text-foreground mb-2">
                                Inquire via the platform
                            </div>
                            <p className="font-app text-xs text-muted-foreground mb-4">
                                Direct buyer-seller contact is not allowed on Bula Real Estate Fiji. Every conversation is
                                routed through us so the listing agent and the seller stay protected.
                            </p>
                            <Button asChild className="w-full bg-coral hover:bg-coral-deep text-white font-app" data-testid="private-listing-inquire-btn">
                                <Link to={`/contact?listing=${listing.id}&private=1`}>
                                    Inquire via Platform
                                </Link>
                            </Button>
                            <p className="font-app text-[10px] text-muted-foreground mt-3 text-center">
                                <Eye className="h-3 w-3 inline mr-1" />
                                {listing.views || 0} private views to date
                            </p>
                        </Card>
                    </div>
                </div>
            </section>
        </div>
    );
}
